/**
 * page-nav.js
 * Drives the `page-nav` section (pills + banner layouts):
 *   - Sticky/dock: pins the nav bar below the sticky header once its natural
 *     top scrolls past the header. A zero-height spacer before the bar marks
 *     that point and reserves the bar's height while pinned, so content below
 *     never jumps. Pills pin on reaching the section; banner pins once the
 *     heading has scrolled past (the bar sits below the heading).
 *   - Scroll-spy: highlights the nav link whose in-page (#anchor) target is
 *     currently scrolled to.
 *   - Smooth-scroll: anchor clicks scroll to the target, offset by the header.
 *   - Center-active: keeps the active pill centered in the horizontal scroller.
 *
 * Scoped per section instance via [data-section-id]; safe with multiple
 * instances on one page and re-runs on shopify:section:load in the editor.
 */
(function () {
  'use strict';

  var SCROLL_SPY_GAP = 20; // px breathing room below the pinned bar

  function headerOffset() {
    var header = document.querySelector('.header--sticky');
    if (!header) return 0;
    // Use the header's live bottom edge, not its height: when the announcement
    // region slides away on scroll-down the height is unchanged but the visible
    // bottom drops, so reading .bottom keeps this bar pinned flush to the header.
    return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
  }

  function initSection(root) {
    if (!root || root.dataset.pageNavReady === 'true') return;
    root.dataset.pageNavReady = 'true';

    var enableSticky = root.dataset.enableSticky !== 'false';
    var bar = root.querySelector('[data-page-nav-bar]');
    var spacer = root.querySelector('[data-page-nav-spacer]');
    var list = root.querySelector('[data-page-nav-list]');
    var links = Array.prototype.slice.call(root.querySelectorAll('[data-page-nav-link]'));
    if (!links.length) return;

    var anchorLinks = links.filter(function (link) {
      var href = link.getAttribute('href') || '';
      return href.charAt(0) === '#' && href.length > 1;
    });

    var isScrollingFromClick = false;
    var clickTimer = null;
    var ticking = false;

    function setActive(target) {
      if (!target) return;
      links.forEach(function (link) { link.classList.remove('is-active'); });
      target.classList.add('is-active');
      centerActivePill(target);
    }

    function centerActivePill(pill) {
      if (!list) return;
      var listRect = list.getBoundingClientRect();
      var pillRect = pill.getBoundingClientRect();
      var scrollLeft = list.scrollLeft + (pillRect.left - listRect.left) - (listRect.width / 2) + (pillRect.width / 2);
      list.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }

    function updateSticky(offset) {
      if (!enableSticky || !bar || !spacer) return;
      var natural = spacer.getBoundingClientRect().top;
      var shouldStick = natural <= offset;
      var isStuck = bar.classList.contains('is-stuck');

      if (shouldStick && !isStuck) {
        spacer.style.height = bar.offsetHeight + 'px';
        bar.classList.add('is-stuck');
      } else if (!shouldStick && isStuck) {
        bar.classList.remove('is-stuck');
        spacer.style.height = '0px';
      }
      if (bar.classList.contains('is-stuck')) {
        bar.style.top = offset + 'px';
      }
    }

    function updateSpy(offset) {
      if (isScrollingFromClick || !anchorLinks.length) return;
      var current = null;
      anchorLinks.forEach(function (link) {
        var target = resolveTarget(link.getAttribute('href'));
        if (target) {
          var top = target.getBoundingClientRect().top - offset - SCROLL_SPY_GAP;
          if (top <= 0) current = link;
        }
      });
      if (current && !current.classList.contains('is-active')) setActive(current);
    }

    function onScroll() {
      var offset = headerOffset();
      updateSticky(offset);
      updateSpy(offset);
      ticking = false;
    }

    function requestTick() {
      if (!ticking) {
        window.requestAnimationFrame(onScroll);
        ticking = true;
      }
    }

    // Smooth-scroll for in-page anchors. Browsers honour prefers-reduced-motion
    // automatically for `behavior: 'smooth'`, so no extra branch is needed.
    anchorLinks.forEach(function (link) {
      link.addEventListener('click', function (e) {
        var target = resolveTarget(link.getAttribute('href'));
        if (!target) return;
        e.preventDefault();
        setActive(link);
        isScrollingFromClick = true;
        var offset = headerOffset();
        var top = target.getBoundingClientRect().top + window.pageYOffset - offset - SCROLL_SPY_GAP;
        window.scrollTo({ top: top, behavior: 'smooth' });
        if (history.pushState) history.pushState(null, '', link.getAttribute('href'));
        if (clickTimer) clearTimeout(clickTimer);
        clickTimer = setTimeout(function () { isScrollingFromClick = false; }, 800);
      });
    });

    function activateFromHash() {
      if (!window.location.hash) return;
      var match = anchorLinks.filter(function (link) {
        return link.getAttribute('href') === window.location.hash;
      })[0];
      if (match) setActive(match);
    }

    window.addEventListener('hashchange', activateFromHash);
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick);

    activateFromHash();
    onScroll();
  }

  function resolveTarget(href) {
    if (!href || href.charAt(0) !== '#' || href.length < 2) return null;
    try {
      return document.querySelector(href);
    } catch {
      return null;
    }
  }

  function initAll() {
    Array.prototype.slice
      .call(document.querySelectorAll('.page-nav[data-section-id]'))
      .forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-init when a section is (re)loaded.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('.page-nav[data-section-id]') : null;
    if (root) {
      root.dataset.pageNavReady = '';
      initSection(root);
    }
  });
})();
